// @flow
import React, { Component } from 'react';
import authAction from 'app/common/react/components/limitedLogin/authAction';
import SendObjectLoader from 'app/common/react/components/sending/SendObjectLoader';
import type { SubjectType } from 'app/common/lib/react/hocTypes';
import { MODAL_ZINDEX } from 'app/common/react/ui/Modal';
import { SendObjectTypes } from 'app/common/react/components/sending/SendObjectTypes';
import { Box, Flyout, Layer } from 'gestalt';

export type BackgroundColor = 'transparent' | 'lightGray' | 'white' | 'transparentDarkGray';

// These props will be passed to Subject, which is the button to render
export type SendShareFlyoutButtonProps = {
  handleSendShareFlyoutButtonClick: () => void,
  iconColor: 'gray' | 'darkGray' | 'red' | 'blue' | 'white',
  backgroundColor: BackgroundColor,
  viewParameter?: number,
  viewType?: number,
};

type BoardProps = {
  id: string,
  name: string,
  url: string,
  description: string,
  imageUrl: string,
};
type FlyoutProps = {
  inModal?: boolean,
  onToggleFlyout: () => void,
  onFlyoutDismiss: () => void,
  onClick?: () => void,
  shownWithinMasonry?: boolean,
  isFlyoutOpen: boolean,
  shareButtonRef?: *,
  onExternalSend?: () => void,
};
// The type of `LikeController`
type SendShareControllerProps<SubjectProps> = FlyoutProps &
  (
    | {
        pinId: string,
        gridDescription?: string,
        imgSrc?: string,
        sendType: 'pin',
      }
    | {
        userId: string,
        gridDescription?: string,
        imgSrc?: string,
        sendType: 'user',
        fullName: string,
        profileUrl: string,
      }
    | {
        board: BoardProps,
        sendType: 'board',
      }
  ) &
  SubjectProps;

// The resulting type from `makeLikeButton`
type SendShareControllerPropsWithSubject<SubjectProps> = {
  Subject: SubjectType<SubjectProps, SendShareFlyoutButtonProps>,
} & SendShareControllerProps<SubjectProps>;

export default class SendShareController extends Component<SendShareControllerPropsWithSubject<*>> {
  static makeSendShareButton(subject: SubjectType<*, *>) {
    return (props: SendShareControllerProps<*>) => (
      <this ref={props.shareButtonRef} Subject={subject} {...props} />
    );
  }

  constructor(props: SendShareControllerPropsWithSubject<*>, context: any) {
    super(props, context);
    this.flyoutAnchor = undefined;
  }

  toggle() {
    const { onToggleFlyout } = this.props;
    onToggleFlyout();
  }

  handleDismiss = () => {
    const { onFlyoutDismiss } = this.props;
    onFlyoutDismiss();
  };

  handleClick = authAction((event: SyntheticEvent<>) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    this.toggle();
  });

  flyoutAnchor: ?HTMLElement;

  flyoutProps = (size: string) => {
    const {
      gridDescription,
      imageSrc,
      pinId,
      userId,
      board,
      sendType,
      fullName,
      profileUrl,
      viewParameter,
      viewType,
      onClick = () => {},
      onExternalSend,
    } = this.props;

    let sendObject = {
      description: gridDescription,
      id: pinId,
      imageUrl: imageSrc,
      name: '',
      type: SendObjectTypes.PIN,
      url: `/pin/${pinId}/`,
    };
    let type = SendObjectTypes.PIN;

    switch (sendType) {
      case 'user':
        sendObject = {
          description: gridDescription,
          id: userId,
          imageUrl: imageSrc,
          name: fullName,
          type: SendObjectTypes.PINNER,
          url: profileUrl,
        };
        type = SendObjectTypes.USER;
        break;
      case 'board':
        sendObject = board && {
          description: board.description,
          id: board.id,
          imageUrl: board.imageUrl,
          name: board.name,
          type: SendObjectTypes.BOARD,
          url: board.url,
        };
        type = SendObjectTypes.BOARD;
        break;
      case 'pin':
      default:
        break;
    }

    return ({
      anchor: this.flyoutAnchor,
      idealDirection: 'down',
      onDismiss: this.handleDismiss,
      size,
      children: (
        <SendObjectLoader
          classNames="SendPin"
          isFlyout
          onHide={() => {
            onClick();
            this.handleDismiss();
          }}
          onExternalSend={onExternalSend}
          sendType={type}
          sharedObject={sendObject}
          viewParameter={viewParameter}
          viewType={viewType}
        />
      ),
    }: $Exact<*>);
  };

  render() {
    const { Subject, inModal, isFlyoutOpen, shownWithinMasonry } = this.props;

    return (
      <div>
        {
          <div
            ref={(div) => {
              if (div) {
                this.flyoutAnchor = div;
              }
            }}
          >
            {<Subject handleSendShareFlyoutButtonClick={this.handleClick} {...this.props} />}
          </div>
        }

        {isFlyoutOpen &&
          (shownWithinMasonry ? (
            <Layer>
              <Box
                dangerouslySetInlineStyle={
                  inModal ? { __style: { zIndex: MODAL_ZINDEX, position: 'absolute' } } : undefined
                }
              >
                <Flyout {...this.flyoutProps('lg')} positionRelativeToAnchor={false} />
              </Box>
            </Layer>
          ) : (
            <Flyout {...this.flyoutProps('lg')} />
          ))}
      </div>
    );
  }
}
